/**
 * Every sim tunable in one place. Values from docs/design/sim-emergence.md §8
 * and docs/design/tech-architecture.md §9; expect heavy tuning via the seed
 * harness and debug overlay.
 */
export const PARAMS = {
  // world
  WORLD_SIZE: 96, // world spans [-48, 48] on x/z
  ISLAND_RADIUS: 44, // beyond this the terrain dips under water

  // clock
  TICK_HZ: 30,
  TICK_MS: 1000 / 30,

  // population
  MAX_AGENTS: 4096, // SoA slot count; slots are monotonic (no death, no recycling)
  INSTANCE_CAP: 1024, // renderer-side instanced mesh capacity
  N_FULL: 200, // Tier A full-AI agents (device-scaled 120-300)

  // items
  MAX_ITEMS: 64,
  MAX_GRIME: 20,

  // needs (0..255 scale, decay per minute) — active-play pacing; the
  // long-horizon Tamagotchi rates arrive with the offline sim tuning pass
  HUNGER_DECAY: 6.0,
  CLEAN_DECAY: 3.5,
  FUN_DECAY: 5.0,
  NEED_LOW: 140, // below this a creature starts seeking a fix
  MOOD_SOFTMIN_K: 40,

  // verbs
  MAX_APPLES: 16, // uneaten apples on the ground; older ones rot away
  APPLE_ROT_S: 240,
  APPLE_BITES: 3,
  APPLE_BITE_HUNGER: 90,
  SCRUB_CLEAN_BOOST: 80,
  BALL_KICK_FUN: 35,
  PET_FUN: 12,

  // rest & circadian
  REST_DECAY: 1.6, // per minute; x1.5 at night, x3 while playing
  DAY_SEC: 1200, // 20-minute day
  SLEEP_REST_REFILL: 6, // per second asleep

  // life-state ladder (mood thresholds; hysteresis lives in the timers)
  THRIVE_MOOD: 190,
  GLUM_MOOD: 120,
  GRIMY_MOOD: 60,
  DORMANT_MOOD: 25,
  GRIMY_AFTER_S: 150, // mood <= GRIMY_MOOD this long -> GRIMY
  DORMANT_AFTER_S: 420, // mood <= DORMANT_MOOD this long -> moss ball
  WAKE_SCRUB_POINTS: 22, // scrub points near a dormant creature to revive
  WAKE_NEED_REFILL: 150,

  // mitosis
  SPLIT_BASE_SEC: 240, // full charge in ~4 min at perfect mood
  SPLIT_MOOD_MIN: 150, // below this (mid-content) the charge doesn't move
  SPLIT_MOOD_FULL: 215, // at/above this the charge fills at full speed
  SPLIT_K0: 24, // initial carrying capacity (structures raise it in M5)
  SPLIT_COOLDOWN_S: 180,
  SPLIT_COOLDOWN_PER_N_S: 1,
  SPLIT_LOCAL_MAX: 6, // neighbors within 2u that stall splitting
  SPLIT_NEED_COST: 30,
  JUVENILE_SCALE_Q: 110,
  ADULT_SCALE_Q: 200,
  GROWTH_S: 150, // juvenile -> adult seconds
} as const;
